import userManager, { login } from "./authService";
import type { NavigateFunction } from "react-router-dom";

/**
 * Error thrown when the user session has expired or is missing.
 * Callers in try/catch blocks will silently handle this since
 * a login redirect has already been triggered.
 */
export class AuthSessionExpiredError extends Error {
    constructor(message = "Session expired") {
        super(message);
        this.name = "AuthSessionExpiredError";
    }
}

// Mutex: ensures only one signinSilent() runs at a time across concurrent API calls
let refreshPromise: Promise<void> | null = null;

async function refreshTokenOnce(): Promise<void> {
    if (refreshPromise) return refreshPromise;
    refreshPromise = userManager.signinSilent()
        .then(() => {})
        .catch((error) => {
            console.error("Échec du rafraîchissement de session", error);
            login();
        })
        .finally(() => { refreshPromise = null; });
    return refreshPromise;
}

const buildAuthOptions = (options: RequestInit, accessToken: string): RequestInit => ({
    ...options,
    headers: {
        ...options.headers,
        Authorization: `Bearer ${accessToken}`,
    },
});

// Retry policy pendant rolling update / hiccup réseau :
//   - GET/HEAD/PUT/DELETE : retry sur 502/503/504/408 + network errors
//   - POST/PATCH : retry UNIQUEMENT sur network error (TypeError fetch),
//     car la requête n'a pas atteint le serveur. Pas de retry sur 5xx
//     pour POST → évite double création / double effet de bord.
const RETRY_STATUSES = new Set([502, 503, 504, 408]);
const SAFE_METHODS = new Set(["GET", "HEAD", "PUT", "DELETE"]);
const BACKOFF_MS = [200, 500, 1200];

const isMethodIdempotent = (method: string | undefined): boolean =>
    SAFE_METHODS.has((method || "GET").toUpperCase());

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
const jitter = (base: number) => base + Math.floor((Math.random() - 0.5) * 100);

async function fetchWithRetry(url: string, init: RequestInit): Promise<Response> {
    const idempotent = isMethodIdempotent(init.method);
    let lastError: unknown = null;

    for (let attempt = 0; attempt <= BACKOFF_MS.length; attempt++) {
        try {
            const response = await fetch(url, init);
            if (idempotent && RETRY_STATUSES.has(response.status) && attempt < BACKOFF_MS.length) {
                await sleep(jitter(BACKOFF_MS[attempt]));
                continue;
            }
            return response;
        } catch (err) {
            // Network error (TypeError) — la requête n'a pas atteint le serveur,
            // donc safe to retry pour TOUS les verbes y compris POST.
            lastError = err;
            if (attempt < BACKOFF_MS.length) {
                await sleep(jitter(BACKOFF_MS[attempt]));
                continue;
            }
            throw err;
        }
    }
    throw lastError ?? new Error("apiFetch retry exhausted");
}

const apiFetch = async (url: string, options: RequestInit = {}, _navigate?: NavigateFunction) => {
    const user = await userManager.getUser();

    if (!user || !user.access_token) {
        console.error("Pas authentifié ! Redirection en cours...");
        await refreshTokenOnce();
        throw new AuthSessionExpiredError("No active session");
    }

    let response = await fetchWithRetry(url, buildAuthOptions(options, user.access_token));

    // Le token a pu expirer entre getUser() et fetch() (race avec automaticSilentRenew).
    // On tente un renew puis on rejoue la requête UNE seule fois avant d'abandonner.
    if (response.status === 401) {
        await refreshTokenOnce();
        const renewed = await userManager.getUser();
        if (renewed?.access_token) {
            response = await fetchWithRetry(url, buildAuthOptions(options, renewed.access_token));
        }
        if (response.status === 401) {
            throw new AuthSessionExpiredError("Unauthorized (401)");
        }
    }

    return response;
};

export default apiFetch;
